import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../features/theme/context/ThemeContext';
import { openMidusOrWeb } from '../utils/common';
import nvgtrLogo_Black from '../assets/nvgtrLogo_Black.svg';
import nvgtrLogo_White from '../assets/nvgtrLogo_White.svg';
import NvgtrShimmerLoader from '../features/chat/components/NvgtrShimmerLoader';
import { navigateToStatusHome } from '../utils/publicStatusPage';

const UnsubscribePage = () => {
  const { darkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const token = searchParams.get('token');

  // Helper function to ensure consistent API URL format
  const getApiUrl = useCallback((endpoint) => {
    // Get the base API URL 
    const apiUrl = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'development' ? 'http://192.168.86.56:8081' : 'https://api.midus.co');
    
    // Clean the URL and normalize format
    let baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    
    // Check if the baseUrl already includes '/api' and handle endpoint appropriately
    if (baseUrl.endsWith('/api')) {
      // Remove '/api' from the beginning of the endpoint if present
      const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
      return `${baseUrl}/${cleanEndpoint}`;
    }
    
    // If endpoint doesn't start with 'api/', add it
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
    return `${baseUrl}/${cleanEndpoint}`;
  }, []);

  // Process unsubscribe request
  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!token) {
        setError('Invalid unsubscribe link. No token provided.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(getApiUrl(`status/unsubscribe?token=${token}`));
        const data = await response.json();

        if (data.success) {
          setResult({
            success: true,
            message: data.message,
            email: data.email,
            alreadyUnsubscribed: data.alreadyUnsubscribed
          });
        } else {
          setError(data.message || 'Failed to process unsubscribe request.');
        }
      } catch (err) {
        console.error('Unsubscribe error:', err);
        setError('Failed to process unsubscribe request. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    processUnsubscribe();
  }, [token, getApiUrl]);

  const handleGoToStatus = () => {
    navigateToStatusHome(navigate);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white dark:bg-[#111111]">
        <NvgtrShimmerLoader darkMode={darkMode} size={60} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#111111] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#d1d5db] dark:border-[#282828]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src={darkMode ? nvgtrLogo_White : nvgtrLogo_Black}
                alt="Midus" 
                className="h-6"
              />
            </div>
            <button 
              onClick={handleGoToStatus}
              className="text-xs text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors"
            >
              View status page
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full text-center">
          {error ? (
            // Error State
            <div className="space-y-6">
              <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-950/20 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <div>
                <h1 className="text-2xl font-semibold text-[#181818] dark:text-white mb-2">
                  Unsubscribe Failed
                </h1>
                <p className="text-[#6b7280] dark:text-[#9ca3af]">
                  {error}
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleGoToStatus}
                  className="w-full px-4 py-2 bg-[#181818] dark:bg-white text-white dark:text-[#181818] rounded-xl hover:bg-[#181818]/80 dark:hover:bg-white/80 transition-colors"
                >
                  Go to Status Page
                </button>
                <a
                  href="midus://"
                  onClick={() => openMidusOrWeb()}
                  className="block w-full px-4 py-2 text-center text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors rounded-xl"
                >
                  Go to Midus
                </a>
              </div>
            </div>
          ) : result?.success ? (
            // Success State
            <div className="space-y-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                result.alreadyUnsubscribed 
                  ? 'bg-blue-100 dark:bg-blue-950/20' 
                  : 'bg-green-100 dark:bg-green-950/20'
              }`}>
                {result.alreadyUnsubscribed ? (
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              
              <div>
                <h1 className="text-2xl font-semibold text-[#181818] dark:text-white mb-2">
                  {result.alreadyUnsubscribed ? 'Already Unsubscribed' : 'Successfully Unsubscribed'}
                </h1>
                <p className="text-[#6b7280] dark:text-[#9ca3af] mb-4">
                  {result.message}
                </p>
                {result.email && (
                  <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
                    Email: <span className="font-medium">{result.email}</span>
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleGoToStatus}
                  className="w-full px-4 py-2 bg-[#181818] dark:bg-white text-white dark:text-[#181818] rounded-xl hover:bg-[#181818]/80 dark:hover:bg-white/80 transition-colors"
                >
                  Go to Status Page
                </button>
                <a
                  href="midus://"
                  onClick={() => openMidusOrWeb()}
                  className="block w-full px-4 py-2 text-center text-[#6b7280] dark:text-[#9ca3af] hover:text-[#181818] dark:hover:text-white transition-colors rounded-xl"
                >
                  Go to Midus
                </a>
              </div>

              {!result.alreadyUnsubscribed && (
                <div className="mt-8 p-4 bg-[#f9fafb] dark:bg-[#181818] rounded-xl border border-[#d1d5db] dark:border-[#282828]">
                  <h3 className="text-sm font-medium text-[#181818] dark:text-white mb-2">
                    What happens next?
                  </h3>
                  <ul className="text-xs text-[#6b7280] dark:text-[#9ca3af] space-y-1">
                    <li>• You will no longer receive status update emails</li>
                    <li>• You can still view our status page anytime</li>
                    <li>• You can resubscribe at any time from the status page</li>
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#d1d5db] dark:border-[#282828] mt-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-sm text-[#6b7280] dark:text-[#9ca3af]">
            Questions? Contact us at{' '}
            <a 
              href="mailto:support@heymidus.com" 
              className="text-[#FFBDA3] hover:text-[#FFBDA3] dark:text-[#FFBDA3] dark:hover:text-[#FFBDA3]"
            >
              support@heymidus.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
